import {
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { useQueryPayee } from "../../../../../query/payee/queries/useQueryPayee";
import { usePayeeEngagementByPayeeIdQuery } from "../../../../../query/payeeEngagements/queries/usePayeeEngagementByPayeeIdQuery";
import { useCollaboratorGroupsQuery } from "../../../../../query/payments/queries";
import { WSQueries } from "../../../../../query/WSQuery";
import { FieldView } from "../../../../../shared/components/FieldView/FieldView";
import { ResourceHistory } from "../../../../../shared/components/ResourceHistory";
import { usePayeeEngagementHistoryResources } from "../../../hooks/usePayeeEngagementHistoryResources";
import { getPayeeName } from "../../../selectors/getPayeeNames";
import { selectorPayeeEngagementNameTmp } from "../../../selectors/selectorPayeeEngagementsTmp";
import { selectorPayerPayeeEngagementName } from "../../../selectors/selectorPayerPayeeEngagementName";
import {
  PAYEES_ROOT_PATH,
  useGoToEngagementDetails,
  useGoToPayeeDetails
} from "../../../utils";

export const RoutePayeeEngagementHistory: React.FC<
  RouteComponentProps<{ payeeId: string; engagementId: string }>
> = ({
  match: {
    params: { payeeId, engagementId }
  },
  history
}) => {
  const queryPayee = useQueryPayee(payeeId);
  const queryEngagement = usePayeeEngagementByPayeeIdQuery(
    payeeId,
    engagementId
  );
  const goToPayeeDetails = useGoToPayeeDetails();
  const goToEngagementDetails = useGoToEngagementDetails();
  const historyResources = usePayeeEngagementHistoryResources(engagementId);
  const queryGroups = useCollaboratorGroupsQuery();

  const isEngagementActive =
    queryEngagement.data?.status !== PayerPayeeEngagementStatus.Inactive;

  return (
    <WSPage>
      <WSQueries queries={{ queryPayee, queryEngagement, queryGroups }}>
        {({
          queryPayeeData: payee,
          queryEngagementData: engagement,
          queryGroupsData
        }) => {
          const engagementName = isEngagementActive
            ? selectorPayerPayeeEngagementName(engagement, true)
            : selectorPayeeEngagementNameTmp(engagement, queryGroupsData, true);

          const groupNames = queryGroupsData
            .filter(group =>
              engagement.collaboratorGroupIds?.includes(
                group.collaboratorGroupId
              )
            )
            .map(group => group.name);

          return (
            <>
              <WSPageToolbar
                title="Engagement history"
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_ROOT_PATH);
                    }
                  },
                  {
                    label: getPayeeName(payee),
                    onClick: () => {
                      goToPayeeDetails(payeeId);
                    }
                  },
                  {
                    label: engagementName || "Engagement",
                    onClick: () => {
                      goToEngagementDetails(payeeId, engagementId);
                    }
                  },
                  {
                    label: "Engagement history"
                  }
                ]}
              />

              <WSGrid>
                <WSGrid.Item span={{ l: "8" }}>
                  <WSList gap="2XL">
                    <WSElement>
                      <WSElement>
                        <WSSectionToolbar title="Engagement information" />
                        <WSPanel>
                          <WSFlexBox gap="XL">
                            <FieldView
                              label="Engagement name"
                              value={engagementName}
                            />
                          </WSFlexBox>
                          {groupNames.length ? (
                            <FieldView
                              mt="XL"
                              label={`Group${groupNames.length > 1 ? "s" : ""}`}
                              value={groupNames.join(", ")}
                            />
                          ) : null}
                        </WSPanel>
                      </WSElement>
                    </WSElement>

                    <ResourceHistory resources={historyResources} />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
